/**
 * The transcript, tool traffic included.
 *
 * `chatMessageSchema` puts the tool calls on the wire on purpose — §2.4's
 * evidence spine applies to chat too: "a user who cannot see what was consulted
 * cannot judge the answer". So this renders three kinds of row, not one:
 *
 *  - **user** — a right-aligned bubble.
 *  - **assistant** — the prose, plus (when the turn was dropped) the reason in
 *    the user's own language instead of an empty bubble.
 *  - **tool** — a collapsed one-line disclosure, "Searched your timeline",
 *    expandable to the arguments and the raw result.
 *
 * ## Why the tool rows are collapsed by DEFAULT but never hidden
 *
 * Eight lookups per turn would bury a two-sentence answer under a wall of JSON,
 * and a user asking "who should I follow up with" does not want to read a
 * dossier dump. But removing them entirely would make every reply unauditable,
 * which is the thing that separates this from a chatbot pasted over a database.
 * `<details>` is the whole mechanism — no state, keyboard-operable for free, and
 * it survives a re-render without a controlled-open bug.
 *
 * ## Why the assistant's own `toolCalls` are folded into the tool rows
 *
 * An assistant turn that requested tools carries `toolCalls` (what it asked for)
 * and the matching `tool` rows carry the results. Rendering the request as its
 * own empty assistant bubble — which is what the raw sequence looks like, since
 * `content` is `''` on a pure tool-call turn — would put a blank speech bubble in
 * the transcript for every hop. Those turns are dropped here and the ARGUMENTS
 * are attached to the tool row instead, which is where a reader looks for them.
 */

import type { ChatMessageDto } from '@crm/contracts';
import { Badge, cn, EmptyState } from '@crm/ui';
import { BotIcon, SparklesIcon, WrenchIcon } from 'lucide-react';
import type * as React from 'react';
import { formatDateTime } from '../../lib/format.ts';
import { dropReasonText, toolLabel } from './chat-vocabulary.ts';

export type ChatTranscriptProps = {
  readonly messages: readonly ChatMessageDto[];
  /** A message typed and sent but not yet answered. Rendered as a real bubble. */
  readonly pendingUserMessage?: string | null;
  readonly isSending?: boolean;
  readonly emptyHint?: string;
};

/**
 * True for an assistant turn whose only content was a tool request.
 *
 * Not `content === ''` alone: a model can legitimately answer with an empty
 * string and a drop reason, and that row must still render (it is the failure
 * the user needs to see).
 */
function isBareToolRequest(message: ChatMessageDto): boolean {
  return (
    message.role === 'assistant' &&
    message.content.trim() === '' &&
    message.droppedReason === null &&
    (message.toolCalls?.length ?? 0) > 0
  );
}

/** `{"query":"priya"}` → `query: priya`. Falls back to the raw string. */
function readableArgs(raw: string): string {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return raw;
    }
    const parts = Object.entries(parsed as Record<string, unknown>).map(
      ([key, value]) => `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`,
    );
    return parts.length === 0 ? 'no filters' : parts.join(' · ');
  } catch {
    return raw;
  }
}

function ToolRow({ message, argsFor }: { message: ChatMessageDto; argsFor: string | null }) {
  const name = message.toolName ?? '';
  const failed = message.toolOk === false;

  return (
    <details
      data-testid="chat-tool-call"
      className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs"
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center gap-2 text-muted-foreground',
          // A 44px row: this is the densest tappable thing on the screen.
          'min-h-9 [@media(pointer:coarse)]:min-h-11',
        )}
      >
        <WrenchIcon className="size-3.5 shrink-0" aria-hidden />
        <span className={cn('truncate', failed && 'text-destructive')}>{toolLabel(name)}</span>
        {failed ? <Badge variant="destructive">failed</Badge> : null}
        <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide opacity-60">
          details
        </span>
      </summary>
      {argsFor === null ? null : (
        <p className="mt-2 text-muted-foreground">
          <span className="font-medium text-foreground">Looked for </span>
          {readableArgs(argsFor)}
        </p>
      )}
      <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-background p-2 text-[11px] text-muted-foreground">
        {message.content === '' ? 'No result recorded.' : message.content}
      </pre>
    </details>
  );
}

export function ChatTranscript({
  messages,
  pendingUserMessage = null,
  isSending = false,
  emptyHint,
}: ChatTranscriptProps) {
  /**
   * `toolName` → the arguments the assistant asked with, taken from the most
   * recent assistant turn that requested it. The wire gives no call id, so this
   * is a name match — which is right for a bounded loop that makes at most eight
   * calls, and degrades to "no arguments shown" rather than to a wrong pairing
   * if the same tool is called twice in one turn.
   */
  const argsByTool = new Map<string, string>();
  const rows: React.ReactNode[] = [];

  for (const message of messages) {
    for (const call of message.toolCalls ?? []) {
      argsByTool.set(call.name, call.arguments);
    }

    if (isBareToolRequest(message)) {
      continue;
    }

    if (message.role === 'tool') {
      rows.push(
        <ToolRow
          key={message.id}
          message={message}
          argsFor={argsByTool.get(message.toolName ?? '') ?? null}
        />,
      );
      continue;
    }

    if (message.role === 'user') {
      rows.push(
        <div key={message.id} className="flex justify-end">
          <p
            data-testid="chat-user-message"
            className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm text-primary-foreground"
          >
            {message.content}
          </p>
        </div>,
      );
      continue;
    }

    rows.push(
      <div key={message.id} className="flex flex-col gap-1">
        <div className="flex items-start gap-2">
          <BotIcon className="mt-1 size-4 shrink-0 text-primary" aria-hidden />
          {message.droppedReason === null ? (
            <p
              data-testid="chat-assistant-message"
              className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm"
            >
              {message.content}
            </p>
          ) : (
            <p
              role="status"
              data-testid="chat-dropped"
              className="max-w-[85%] rounded-2xl rounded-bl-sm border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
            >
              {dropReasonText(message.droppedReason)}
            </p>
          )}
        </div>
        {message.toolLimitReached ? (
          <p className="ml-6 text-xs text-muted-foreground">
            Hermes hit its limit of eight lookups for this message, so the answer may be partial.
            Asking something narrower usually helps.
          </p>
        ) : null}
        <span className="ml-6 text-[10px] text-muted-foreground">
          {formatDateTime(message.createdAt) ?? ''}
        </span>
      </div>,
    );
  }

  if (rows.length === 0 && pendingUserMessage === null && !isSending) {
    return (
      <EmptyState
        icon={<SparklesIcon aria-hidden />}
        title="Ask Hermes about your network"
        description={
          emptyHint ??
          'Hermes can search your contacts and timeline, check what you promised people, and draft a follow-up — but it never sends anything or changes a record without asking you first.'
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {rows}
      {pendingUserMessage === null ? null : (
        <div className="flex justify-end">
          <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary/70 px-3 py-2 text-sm text-primary-foreground">
            {pendingUserMessage}
          </p>
        </div>
      )}
      {isSending ? (
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <BotIcon className="size-4 animate-pulse text-primary" aria-hidden />
          Hermes is looking things up — this takes a few seconds, and up to eight lookups.
        </p>
      ) : null}
    </div>
  );
}
